import type { Category, VendorDefinition } from "../types";
import { ANALYTICS_VENDORS } from "./analytics";
import { EMBED_VENDORS } from "./embeds";
import { MARKETING_VENDORS } from "./marketing";
import { pushConsentDefault, pushConsentUpdate } from "./google-consent-mode";

/** Everything the library knows about. A site enables the subset it actually uses. */
export const REGISTRY: VendorDefinition[] = [
  ...ANALYTICS_VENDORS,
  ...MARKETING_VENDORS,
  ...EMBED_VENDORS,
];

const BY_ID = new Map(REGISTRY.map((vendor) => [vendor.id, vendor]));

export function getVendor(id: string): VendorDefinition | undefined {
  return BY_ID.get(id);
}

/** Resolves configured ids, warning about typos rather than silently skipping them. */
export function resolveVendors(ids: readonly string[], debug: boolean): VendorDefinition[] {
  const resolved: VendorDefinition[] = [];
  for (const id of ids) {
    const vendor = BY_ID.get(id);
    if (vendor) resolved.push(vendor);
    else if (debug) console.warn(`[consentkit] unknown vendor id "${id}" — check the registry.`);
  }
  return resolved;
}

export function vendorsForCategory(
  vendors: readonly VendorDefinition[],
  category: Category,
): VendorDefinition[] {
  return vendors.filter((vendor) => vendor.category === category);
}

/** Vendor ids whose embeds need a click-to-load placeholder. */
export function embedVendors(vendors: readonly VendorDefinition[]): VendorDefinition[] {
  return vendors.filter((vendor) => vendor.embed === true);
}

const GOOGLE_IDS = ["google-analytics", "google-tag-manager", "google-ads"];

/** True when the site loads any Google tag, i.e. when Consent Mode v2 has to be wired up. */
export function usesGoogleTags(vendors: readonly VendorDefinition[]): boolean {
  return vendors.some((vendor) => GOOGLE_IDS.includes(vendor.id));
}

export { pushConsentDefault, pushConsentUpdate };
